/**
 * UpcomingMeetings — Table of scheduled meetings merged with Google Calendar events
 */

import { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp, Play, MoreHorizontal, Calendar, Plus, ExternalLink, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MeetingsTable, type ColumnDef } from './MeetingsTable';
import { useMeetings, useCalendarEvents } from '../hooks/useMeetings';
import { useStaffUserMap } from '../hooks/useStaffUsers';
import { CreateMeetingModal } from './CreateMeetingModal';
import type { CalendarEvent, Meeting } from '../types';

// =============================================================================
// Types
// =============================================================================

type RowSource = 'db' | 'gcal';

interface UnifiedRow {
  key: string;
  source: RowSource;
  scheduledAt: string;
  title: string;
  typeLabel: string;
  ownerId: string | null;
  scribeId: string | null;
  gcalAttendees: CalendarEvent['attendees'];
  meetLink: string | null;
  htmlLink: string | null;
  meeting?: Meeting;
  calEvent?: CalendarEvent;
}

interface ColumnFilters {
  date: string;
  meeting: string;
  type: string;
  participants: string;
  owner: string;
}

// =============================================================================
// Helpers
// =============================================================================

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-CA', {
    timeZone: 'America/Montreal',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-CA', {
    timeZone: 'America/Montreal',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const MEETING_TYPE_LABELS: Record<string, string> = {
  eos: 'EOS',
  sales: 'Sales',
  rnd: 'R&D',
  custom: 'Custom',
};

function buildRows(
  meetings: Meeting[],
  calEvents: CalendarEvent[],
  sortDir: 'asc' | 'desc'
): UnifiedRow[] {
  const dbRows: UnifiedRow[] = meetings.map((m) => ({
    key: `db:${m.id}`,
    source: 'db',
    scheduledAt: m.scheduled_at,
    title: m.title,
    typeLabel: MEETING_TYPE_LABELS[m.type] ?? m.type,
    ownerId: m.owner_id,
    scribeId: m.scribe_id,
    gcalAttendees: [],
    meetLink: null,
    htmlLink: null,
    meeting: m,
  }));

  const dbTimes = meetings.map((m) => new Date(m.scheduled_at).getTime());
  const dbTitles = new Set(meetings.map((m) => m.title.toLowerCase()));

  const gcalRows: UnifiedRow[] = calEvents
    .filter((ev) => {
      if (!ev.start) return false;
      const evTime = new Date(ev.start).getTime();
      const titleMatch = dbTitles.has(ev.summary.toLowerCase());
      const timeMatch = dbTimes.some((t) => Math.abs(t - evTime) < 30 * 60 * 1000);
      return !(titleMatch && timeMatch);
    })
    .map((ev) => ({
      key: `gcal:${ev.id}`,
      source: 'gcal',
      scheduledAt: ev.start,
      title: ev.summary,
      typeLabel: 'Google Calendar',
      ownerId: ev.organizer?.email ?? null,
      scribeId: null,
      gcalAttendees: ev.attendees,
      meetLink: ev.meetLink,
      htmlLink: ev.htmlLink,
      calEvent: ev,
    }));

  const all = [...dbRows, ...gcalRows];
  all.sort((a, b) => {
    const diff = new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime();
    return sortDir === 'asc' ? diff : -diff;
  });
  return all;
}

// =============================================================================
// UpcomingMeetings
// =============================================================================

interface UpcomingMeetingsProps {
  teamId?: string;
  onStartMeeting?: (id: string) => void;
  onScheduleMeeting?: () => void;
}

export function UpcomingMeetings({
  teamId,
  onStartMeeting,
  onScheduleMeeting,
}: UpcomingMeetingsProps) {
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [filters, setFilters] = useState<ColumnFilters>({
    date: '', meeting: '', type: '', participants: '', owner: '',
  });

  const { data: meetingsData, isLoading: meetingsLoading } = useMeetings({
    status: 'scheduled',
    team_id: teamId,
    sortDirection: 'asc',
  });

  const { data: calData, isLoading: calLoading } = useCalendarEvents({ days: 30 });
  const userMap = useStaffUserMap();

  const meetings = meetingsData?.meetings ?? [];
  const calEvents = calData?.events ?? [];
  const allRows = buildRows(meetings, calEvents, sortDirection);
  const isLoading = meetingsLoading || calLoading;
  const calConfigured = calData?.configured !== false;

  function resolveOwner(row: UnifiedRow): string {
    if (!row.ownerId) return '—';
    if (row.source === 'gcal') return row.ownerId;
    const u = userMap.get(row.ownerId);
    if (!u) return '—';
    return [u.firstname, u.lastname].filter(Boolean).join(' ') || u.email;
  }

  function resolveParticipants(row: UnifiedRow): string[] {
    if (row.source === 'gcal') {
      return row.gcalAttendees
        .filter((a) => !a.organizer)
        .map((a) => a.displayName || a.email);
    }
    const ids = [row.ownerId, row.scribeId].filter((id): id is string => !!id);
    return ids.map((id) => {
      const u = userMap.get(id);
      if (!u) return null;
      return [u.firstname, u.lastname].filter(Boolean).join(' ') || u.email;
    }).filter((n): n is string => !!n);
  }

  function setFilter(col: keyof ColumnFilters, value: string) {
    setFilters((prev) => ({ ...prev, [col]: value }));
  }

  function clearFilters() {
    setFilters({ date: '', meeting: '', type: '', participants: '', owner: '' });
  }

  function handleScheduleMeeting() {
    setScheduleOpen(true);
    onScheduleMeeting?.();
  }

  // Client-side column filtering
  const rows = useMemo(() => {
    const f = filters;
    if (!f.date && !f.meeting && !f.type && !f.participants && !f.owner) return allRows;
    return allRows.filter((row) => {
      if (f.date && !formatDate(row.scheduledAt).includes(f.date)) return false;
      if (f.meeting && !row.title.toLowerCase().includes(f.meeting.toLowerCase())) return false;
      if (f.type && !row.typeLabel.toLowerCase().includes(f.type.toLowerCase())) return false;
      if (f.participants) {
        const names = resolveParticipants(row).join(' ').toLowerCase();
        if (!names.includes(f.participants.toLowerCase())) return false;
      }
      if (f.owner) {
        const owner = resolveOwner(row).toLowerCase();
        if (!owner.includes(f.owner.toLowerCase())) return false;
      }
      return true;
    });
  }, [allRows, filters, userMap]);

  const hasFilters = Object.values(filters).some(Boolean);

  const columns: ColumnDef<UnifiedRow>[] = [
    {
      id: 'date',
      header: (
        <button
          className="flex items-center gap-1 hover:text-foreground transition-colors"
          onClick={() => setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))}
        >
          Date
          {sortDirection === 'asc' ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )}
        </button>
      ),
      filterPlaceholder: 'yyyy-mm-dd',
      filterValue: filters.date,
      onFilterChange: (v) => setFilter('date', v),
      cell: (row) => (
        <>
          <div className="font-medium">{formatDate(row.scheduledAt)}</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {row.calEvent?.allDay ? 'All day' : formatTime(row.scheduledAt)}
          </div>
        </>
      ),
    },
    {
      id: 'meeting',
      header: 'Meeting',
      filterPlaceholder: 'Search…',
      filterValue: filters.meeting,
      onFilterChange: (v) => setFilter('meeting', v),
      cell: (row) => <span className="font-medium">{row.title}</span>,
    },
    {
      id: 'type',
      header: 'Type',
      filterPlaceholder: 'Search…',
      filterValue: filters.type,
      onFilterChange: (v) => setFilter('type', v),
      cell: (row) =>
        row.source === 'gcal' ? (
          <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded px-1.5 py-0.5 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800">
            <Video className="h-3 w-3" />
            Google Calendar
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">{row.typeLabel}</span>
        ),
    },
    {
      id: 'participants',
      header: 'Participants',
      filterPlaceholder: 'Search…',
      filterValue: filters.participants,
      onFilterChange: (v) => setFilter('participants', v),
      cell: (row) => {
        const names = resolveParticipants(row);
        if (names.length === 0) return <span className="text-muted-foreground text-sm">—</span>;
        const visible = names.slice(0, 3);
        const extra = names.length - visible.length;
        return (
          <span className="text-muted-foreground text-sm">
            {visible.join(', ')}
            {extra > 0 && <span className="text-muted-foreground/60"> +{extra}</span>}
          </span>
        );
      },
    },
    {
      id: 'owner',
      header: 'Owner',
      filterPlaceholder: 'Search…',
      filterValue: filters.owner,
      onFilterChange: (v) => setFilter('owner', v),
      cell: (row) => <span className="text-muted-foreground text-sm">{resolveOwner(row)}</span>,
    },
    {
      id: 'actions',
      header: '',
      headClassName: 'w-[120px]',
      cell: (row) => (
        <div className="flex items-center gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
          {row.source === 'gcal' ? (
            <>
              {row.meetLink && (
                <Button
                  size="sm"
                  className="h-7 bg-blue-600 hover:bg-blue-700 text-white text-xs"
                  asChild
                >
                  <a href={row.meetLink} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-3 w-3 mr-1" />
                    Join
                  </a>
                </Button>
              )}
              {row.htmlLink && (
                <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
                  <a href={row.htmlLink} target="_blank" rel="noopener noreferrer" title="Open in Google Calendar">
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              )}
            </>
          ) : (
            <>
              <Button
                size="sm"
                className="h-7 bg-purple-600 hover:bg-purple-700 text-white text-xs"
                onClick={() => onStartMeeting?.(row.meeting!.id)}
              >
                <Play className="h-3 w-3 mr-1" />
                Start
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem>Edit</DropdownMenuItem>
                  <DropdownMenuItem className="text-destructive">Cancel</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <MeetingsTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.key}
        isLoading={isLoading}
        total={allRows.length}
        hasFilters={hasFilters}
        onClearFilters={clearFilters}
        emptyState={
          <div className="flex flex-col items-center justify-center py-12 text-center border border-dashed border-border rounded-lg">
            <Calendar className="h-8 w-8 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">No upcoming meetings scheduled.</p>
            <Button size="sm" variant="outline" className="mt-3" onClick={handleScheduleMeeting}>
              Schedule a Meeting
            </Button>
          </div>
        }
        headerActions={
          <Button size="sm" variant="outline" onClick={handleScheduleMeeting}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Schedule a Meeting
          </Button>
        }
        headerExtras={
          !calConfigured ? (
            <span className="text-xs text-muted-foreground border border-dashed border-border rounded px-2 py-0.5">
              Google Calendar not configured
            </span>
          ) : undefined
        }
      />

      <CreateMeetingModal
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        teamId={teamId}
      />
    </>
  );
}
